import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Box,
  VStack,
  HStack,
  Input,
  Button,
  Text,
  Flex,
  IconButton,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Divider,
  Badge,
  Select,
  Textarea,
  useToast,
  Card,
  CardBody,
  Heading,
  Avatar,
  AvatarGroup,
  Icon,
  useBreakpointValue,
  Image
} from '@chakra-ui/react';
import { FiSend, FiRefreshCw, FiMessageSquare, FiUser, FiCpu, FiUpload, FiFile, FiGlobe, FiDownload } from 'react-icons/fi';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface ChatCompletionResponse {
  id?: string;
  object?: string;
  created?: number;
  model?: string;
  choices?: Array<{
    index: number;
    delta?: {
      content?: string;
      role?: string;
    };
    finish_reason?: string;
  }>;
  error?: {
    message: string;
  };
  type?: string;
  data?: any;
  content?: string;
}

interface FileAnalysisResponse {
  filename: string;
  file_size_bytes: number;
  extracted_text_length: number;
  prompt_tokens: number;
  max_tokens: number;
  prompt_preview: string;
  supported_formats: string[];
  extracted_text?: string; // Add the actual extracted text
}

// Model serving this page
const MODEL_ID = 'Qwen/Qwen3.5-35B-A3B';

const GptOssBeta: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentStreamingMessage, setCurrentStreamingMessage] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileAnalysis, setFileAnalysis] = useState<FileAnalysisResponse | null>(null);
  const [isAnalyzingFile, setIsAnalyzingFile] = useState(false);
  const [isProcessingCellebrite, setIsProcessingCellebrite] = useState(false);
  const [cellebriteFile, setCellebriteFile] = useState<File | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState('Dominican Spanish');
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [translatedContent, setTranslatedContent] = useState<string>('');
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [chunkProgress, setChunkProgress] = useState<{current: number, total: number}>({current: 0, total: 0});
  const [completedChunks, setCompletedChunks] = useState<Set<number>>(new Set());
  const [htmlStructureData, setHtmlStructureData] = useState<any[]>([]);
  const [htmlContent, setHtmlContent] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cellebriteFileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Language options for translation
  const languageOptions = [
    { value: 'Dominican Spanish', label: 'Dominican Spanish (Bronx/Newark)' },
    { value: 'Mexican Spanish', label: 'Mexican Spanish' },
    { value: 'Puerto Rican Spanish', label: 'Puerto Rican Spanish' },
    { value: 'Cuban Spanish', label: 'Cuban Spanish' },
    { value: 'Colombian Spanish', label: 'Colombian Spanish' },
    { value: 'Venezuelan Spanish', label: 'Venezuelan Spanish' },
    { value: 'Argentine Spanish', label: 'Argentine Spanish' },
    { value: 'Chilean Spanish', label: 'Chilean Spanish' },
    { value: 'Peruvian Spanish', label: 'Peruvian Spanish' },
    { value: 'Ecuadorian Spanish', label: 'Ecuadorian Spanish' },
    { value: 'Guatemalan Spanish', label: 'Guatemalan Spanish' },
    { value: 'Honduran Spanish', label: 'Honduran Spanish' },
    { value: 'Salvadoran Spanish', label: 'Salvadoran Spanish' },
    { value: 'Nicaraguan Spanish', label: 'Nicaraguan Spanish' },
    { value: 'Costa Rican Spanish', label: 'Costa Rican Spanish' },
    { value: 'Panamanian Spanish', label: 'Panamanian Spanish' },
    { value: 'Bolivian Spanish', label: 'Bolivian Spanish' },
    { value: 'Paraguayan Spanish', label: 'Paraguayan Spanish' },
    { value: 'Uruguayan Spanish', label: 'Uruguayan Spanish' },
    { value: 'General Spanish', label: 'General Spanish' },
    { value: 'Portuguese', label: 'Portuguese (Brazil)' },
    { value: 'French', label: 'French' },
    { value: 'Italian', label: 'Italian' },
    { value: 'Arabic', label: 'Arabic' },
    { value: 'Chinese', label: 'Chinese (Mandarin)' },
    { value: 'Russian', label: 'Russian' },
    { value: 'Japanese', label: 'Japanese' },
    { value: 'Korean', label: 'Korean' },
    { value: 'Hindi', label: 'Hindi' },
    { value: 'German', label: 'German' },
    { value: 'Dutch', label: 'Dutch' },
    { value: 'Swedish', label: 'Swedish' },
    { value: 'Norwegian', label: 'Norwegian' },
    { value: 'Danish', label: 'Danish' },
    { value: 'Finnish', label: 'Finnish' },
    { value: 'Polish', label: 'Polish' },
    { value: 'Czech', label: 'Czech' },
    { value: 'Hungarian', label: 'Hungarian' },
    { value: 'Romanian', label: 'Romanian' },
    { value: 'Bulgarian', label: 'Bulgarian' },
    { value: 'Croatian', label: 'Croatian' },
    { value: 'Serbian', label: 'Serbian' },
    { value: 'Slovak', label: 'Slovak' },
    { value: 'Slovenian', label: 'Slovenian' },
    { value: 'Ukrainian', label: 'Ukrainian' },
    { value: 'Turkish', label: 'Turkish' },
    { value: 'Greek', label: 'Greek' },
    { value: 'Hebrew', label: 'Hebrew' },
    { value: 'Thai', label: 'Thai' },
    { value: 'Vietnamese', label: 'Vietnamese' },
    { value: 'Indonesian', label: 'Indonesian' },
    { value: 'Malay', label: 'Malay' },
    { value: 'Tagalog', label: 'Tagalog (Filipino)' },
    { value: 'Swahili', label: 'Swahili' },
    { value: 'Amharic', label: 'Amharic' },
    { value: 'Yoruba', label: 'Yoruba' },
    { value: 'Igbo', label: 'Igbo' },
    { value: 'Hausa', label: 'Hausa' },
    { value: 'Zulu', label: 'Zulu' },
    { value: 'Afrikaans', label: 'Afrikaans' },
    { value: 'Other', label: 'Other (specify in prompt)' }
  ];

  // Responsive values
  const containerMaxW = useBreakpointValue({ base: '100%', md: '1200px' });
  const chatHeight = useBreakpointValue({ base: 'calc(100vh - 200px)', md: '600px' });
  const inputRows = useBreakpointValue({ base: 4, md: 3 });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, currentStreamingMessage]);

  // Add CSS for markdown content
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .markdown-content {
        font-size: 14px;
        line-height: 1.6;
      }
      .markdown-content p {
        margin-bottom: 8px;
      }
      .markdown-content p:last-child {
        margin-bottom: 0;
      }
      .markdown-content code {
        background-color: rgba(0, 0, 0, 0.1);
        padding: 2px 4px;
        border-radius: 3px;
        font-family: 'Courier New', monospace;
        font-size: 12px;
      }
      .markdown-content pre {
        background-color: rgba(0, 0, 0, 0.1);
        padding: 12px;
        border-radius: 6px;
        overflow-x: auto;
        margin: 8px 0;
      }
      .markdown-content pre code {
        background-color: transparent;
        padding: 0;
      }
      .markdown-content ul, .markdown-content ol {
        margin: 8px 0;
        padding-left: 20px;
      }
      .markdown-content li {
        margin: 4px 0;
      }
      .markdown-content blockquote {
        border-left: 4px solid #e2e8f0;
        padding-left: 16px;
        margin: 8px 0;
        color: #666;
      }
      .markdown-content h1, .markdown-content h2, .markdown-content h3, 
      .markdown-content h4, .markdown-content h5, .markdown-content h6 {
        margin: 16px 0 8px 0;
        font-weight: 600;
      }
      .markdown-content h1 { font-size: 18px; }
      .markdown-content h2 { font-size: 16px; }
      .markdown-content h3 { font-size: 15px; }
      .markdown-content h4, .markdown-content h5, .markdown-content h6 { font-size: 14px; }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Set viewport meta tag for mobile optimization
  useEffect(() => {
    // Set viewport meta tag to prevent zooming
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    if (!viewportMeta) {
      viewportMeta = document.createElement('meta');
      viewportMeta.setAttribute('name', 'viewport');
      document.head.appendChild(viewportMeta);
    }
    viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
    
    return () => {
      // Reset viewport meta tag when component unmounts
      if (viewportMeta) {
        viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0');
      }
    };
  }, []);

  const addMessage = (role: 'user' | 'assistant', content: string) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      role,
      content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    addMessage('user', userMessage);
    setIsLoading(true);
    setCurrentStreamingMessage('');

    try {
      // Build conversation history for context
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Add the current user message
      conversationHistory.push({
        role: 'user',
        content: userMessage
      });

      // Prepare system message with file content if available
      let systemMessage = "You are a powerful AI assistant available in an airgapped environment for law enforcement use.";
      
      if (fileAnalysis && fileAnalysis.extracted_text) {
        // Clean and format the file content
        const cleanedText = fileAnalysis.extracted_text
          .replace(/\s+/g, ' ')  // Replace multiple whitespace with single space
          .trim();
        
        // Truncate if too long (keep first 8000 chars to avoid token overflow)
        const maxFileChars = 100000; // ~25K tokens; model context is 262K
        const truncatedText = cleanedText.length > maxFileChars 
          ? cleanedText.substring(0, maxFileChars) + "... [Content truncated for length]"
          : cleanedText;
        
        systemMessage += `\n\nYou have access to the following file content for reference:\n\n` +
          `FILE: ${fileAnalysis.filename}\n` +
          `CONTENT:\n${truncatedText}\n\n` +
          `When answering questions, you can reference information from this file. ` +
          `If the user asks about specific details in the file, provide accurate information based on the content above.`;
      }

      // Use the new API format for port 8015
      const response = await fetch('/api/ollama/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: MODEL_ID,
          messages: [
            {"role": "system", "content": systemMessage},
            ...conversationHistory
          ],
          temperature: 0.5,
          top_p: 0.9,
          max_tokens: 32768,
          stream: true
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      let assistantMessage = '';
      const decoder = new TextDecoder();
      let buffer = '';
      let messageAdded = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data: ChatCompletionResponse = JSON.parse(line.slice(6));
              
              if (data.error) {
                throw new Error(data.error.message);
              }

              if (data.choices && data.choices[0]?.delta?.content) {
                assistantMessage += data.choices[0].delta.content;
                setCurrentStreamingMessage(assistantMessage);
              }

              if (data.choices && data.choices[0]?.finish_reason) {
                if (assistantMessage && !messageAdded) {
                  addMessage('assistant', assistantMessage);
                  messageAdded = true;
                }
                setCurrentStreamingMessage('');
                break;
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }

      // Handle any remaining buffer
      if (buffer.trim()) {
        const lines = buffer.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data: ChatCompletionResponse = JSON.parse(line.slice(6));
              if (data.choices && data.choices[0]?.delta?.content) {
                assistantMessage += data.choices[0].delta.content;
                setCurrentStreamingMessage(assistantMessage);
              }
            } catch (e) {
              console.error('Error parsing final SSE data:', e);
            }
          }
        }
      }

      // Ensure the final message is added only once
      if (assistantMessage && !messageAdded) {
        addMessage('assistant', assistantMessage);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to send message',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      addMessage('assistant', 'Sorry, I encountered an error. Please try again.');
    } finally {
      setIsLoading(false);
      setCurrentStreamingMessage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setCurrentStreamingMessage('');
    setInputValue('');
    setIsLoading(false);
    setUploadedFile(null);
    setFileAnalysis(null);
    setCellebriteFile(null);
    setIsProcessingCellebrite(false);
    setShowLanguageSelector(false);
    setSelectedLanguage('Dominican Spanish');
    setTranslatedContent('');
    setShowDownloadButton(false);
    setChunkProgress({current: 0, total: 0});
    setCompletedChunks(new Set());
    setHtmlStructureData([]);
    setHtmlContent('');
  };

  // Helper function to get consistent colors for senders
  const getSenderColor = (sender: string) => {
    const colorPalette = [
      '#e8f5e8', // Light green
      '#e3f2fd', // Light blue
      '#fff3e0', // Light orange
      '#f3e5f5', // Light purple
      '#e0f2f1', // Light teal
      '#fce4ec', // Light pink
      '#f1f8e9', // Light lime
      '#e8eaf6', // Light indigo
      '#fff8e1', // Light amber
      '#efebe9'  // Light brown
    ];
    
    // Simple hash function to get consistent color for same sender
    let hash = 0;
    for (let i = 0; i < sender.length; i++) {
      hash = ((hash << 5) - hash + sender.charCodeAt(i)) & 0xffffffff;
    }
    return colorPalette[Math.abs(hash) % colorPalette.length];
  };

  // Helper function to generate HTML report
  const generateHTMLReport = (messages: any[]) => {
    const htmlReportContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Cellebrite Translation - ${cellebriteFile?.name || 'Unknown File'}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
            background: #f0f2f5; 
            padding: 20px; 
            line-height: 1.4;
          }
          .header { 
            background: linear-gradient(135deg, #2c5aa0, #1e3a8a); 
            color: white; 
            padding: 30px; 
            border-radius: 12px; 
            margin-bottom: 30px; 
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            display: flex;
            align-items: center;
            gap: 20px;
          }
          .header-logo { 
            width: 60px; 
            height: 60px; 
            flex-shrink: 0;
            background: rgba(255,255,255,0.2);
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 18px;
            color: white;
            text-shadow: 0 1px 2px rgba(0,0,0,0.3);
          }
          .header-content { flex: 1; }
          .header h1 { font-size: 28px; margin-bottom: 15px; font-weight: 600; }
          .header-info { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; }
          .header-item { background: rgba(255,255,255,0.1); padding: 10px; border-radius: 8px; }
          .header-label { font-size: 12px; opacity: 0.8; text-transform: uppercase; letter-spacing: 0.5px; }
          .header-value { font-size: 14px; font-weight: 500; margin-top: 4px; }
          
          .chat-container { 
            max-width: 600px; 
            margin: 0 auto; 
            background: white; 
            border-radius: 12px; 
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            overflow: hidden;
          }
          
          .message { 
            display: flex; 
            margin-bottom: 20px; 
            padding: 15px 20px; 
            border-bottom: 1px solid #f0f0f0;
          }
          .message:last-child { border-bottom: none; }
          
          .message-avatar { 
            width: 40px; 
            height: 40px; 
            border-radius: 50%; 
            display: flex; 
            align-items: center; 
            justify-content: center; 
            color: white; 
            font-weight: bold; 
            font-size: 14px; 
            margin-right: 15px; 
            flex-shrink: 0;
            text-shadow: 0 1px 2px rgba(0,0,0,0.3);
          }
          
          .message-content { flex: 1; }
          .message-header { 
            display: flex; 
            justify-content: space-between; 
            align-items: center; 
            margin-bottom: 8px; 
          }
          .message-sender { 
            font-weight: 600; 
            color: #2c5aa0; 
            font-size: 14px; 
          }
          .message-time { 
            font-size: 12px; 
            color: #666; 
          }
          
          .message-text { 
            padding: 12px 16px; 
            border-radius: 18px; 
            border: 1px solid rgba(0,0,0,0.1); 
            margin-bottom: 8px;
            font-size: 14px;
            line-height: 1.5;
            color: #333;
          }
          
          .translation-text { 
            padding: 12px 16px; 
            border-radius: 18px; 
            border-left: 4px solid rgba(0,0,0,0.2); 
            font-size: 14px;
            line-height: 1.5;
            color: #333;
            font-style: italic;
          }
          
          .translation-label { 
            font-size: 11px; 
            color: #666; 
            text-transform: uppercase; 
            letter-spacing: 0.5px; 
            margin-bottom: 4px; 
            font-weight: 600;
          }
          
          .footer { 
            text-align: center; 
            padding: 20px; 
            color: #666; 
            font-size: 12px; 
            background: #f8f9fa; 
            border-top: 1px solid #e9ecef;
          }
          
          .no-messages { 
            text-align: center; 
            padding: 40px; 
            color: #666; 
            font-style: italic; 
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-logo">RTC</div>
          <div class="header-content">
            <h1>Cellebrite Translation Report</h1>
            <div class="header-info">
              <div class="header-item">
                <div class="header-label">Original File</div>
                <div class="header-value">${cellebriteFile?.name || 'Unknown'}</div>
              </div>
              <div class="header-item">
                <div class="header-label">Source Language</div>
                <div class="header-value">${selectedLanguage}</div>
              </div>
              <div class="header-item">
                <div class="header-label">Translation Date</div>
                <div class="header-value">${new Date().toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="chat-container">
          ${messages.length > 0 ? messages.map((msg, index) => `
            <div class="message">
              <div class="message-avatar" style="background: ${msg.color};">
                ${msg.sender.charAt(0).toUpperCase()}
              </div>
              <div class="message-content">
                <div class="message-header">
                  <div class="message-sender">${msg.sender}</div>
                  <div class="message-time">${msg.timestamp}</div>
                </div>
                <div class="message-text" style="background: ${msg.color};">
                  ${msg.original}
                </div>
                ${msg.translated && msg.translated !== msg.original ? `
                  <div class="translation-label">English Translation:</div>
                  <div class="translation-text" style="background: ${msg.color};">
                    ${msg.translated}
                  </div>
                ` : ''}
              </div>
            </div>
          `).join('') : `
            <div class="no-messages">
              <p>No messages found in the translation content.</p>
            </div>
          `}
        </div>
        
        <div class="footer">
          <p>Generated by RTC Chatbot Translation System</p>
          <p>This report contains translated communications from Cellebrite extraction</p>
        </div>
      </body>
      </html>
    `;

    // Create a blob with the HTML content
    const blob = new Blob([htmlReportContent], { type: 'text/html' });
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Cellebrite_Translation_${cellebriteFile?.name?.replace('.xlsx', '') || 'Unknown'}_${new Date().toISOString().split('T')[0]}.html`;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast({
      title: 'Download Started',
      description: 'HTML report download has started',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const clearFile = () => {
    setUploadedFile(null);
    setFileAnalysis(null);
    toast({
      title: 'File Cleared',
      description: 'File content has been removed from the conversation.',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size before upload (100MB limit)
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      toast({
        title: 'File Too Large',
        description: `File size (${(file.size / (1024 * 1024)).toFixed(1)}MB) exceeds the 100MB limit.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setUploadedFile(file);
    setIsAnalyzingFile(true);
    
    // Reset the file input to prevent caching issues
    if (event.target) {
      event.target.value = '';
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/ollama/file-analyze', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const analysis: FileAnalysisResponse = await response.json();
      setFileAnalysis(analysis);

      toast({
        title: 'File Analyzed',
        description: `${file.name} processed successfully. Estimated tokens: ${analysis.prompt_tokens}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Add a system message about the uploaded file
      addMessage('assistant', `File "${file.name}" uploaded and analyzed. File size: ${(file.size / 1024).toFixed(1)}KB, Estimated tokens: ${analysis.prompt_tokens}, Max tokens: ${analysis.max_tokens}. The file content is now available for reference in our conversation.`);

    } catch (error) {
      console.error('Error analyzing file:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to analyze file',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setUploadedFile(null);
    } finally {
      setIsAnalyzingFile(false);
    }
  };

  const triggerFileUpload = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const triggerCellebriteUpload = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    cellebriteFileInputRef.current?.click();
  };

  const downloadAsWord = () => {
    if (!translatedContent) return;

    // Debug: Log the current state
    console.log('Download function - htmlContent length:', htmlContent.length);
    console.log('Download function - htmlStructureData:', htmlStructureData);
    console.log('Download function - translatedContent length:', translatedContent.length);

    // Check if we have backend-generated HTML content (preferred)
    if (htmlContent) {
      console.log('Using backend-generated HTML content for download');
      
      // Create a blob with the HTML content
      const blob = new Blob([htmlContent], { type: 'text/html' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Cellebrite_Translation_${cellebriteFile?.name?.replace('.xlsx', '') || 'Unknown'}_${new Date().toISOString().split('T')[0]}.html`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Download Started',
        description: 'HTML report download has started',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Check if we have HTML structure data (fallback)
    if (htmlStructureData && htmlStructureData.length > 0) {
      console.log('Using HTML structure data for download');
      const messages = htmlStructureData.map((msg: any) => ({
        sender: msg.sender,
        timestamp: msg.timestamp,
        original: msg.original_message,
        translated: msg.translation,
        color: getSenderColor(msg.sender)
      }));
      
      generateHTMLReport(messages);
      return;
    }

    // Parse the translated content to extract individual messages (fallback for old format)
    const parseTranslatedContent = (content: string) => {
      // Debug: Log the raw content to see what we're working with
      console.log('Raw translated content:', content);
      
      // Split by lines and look for table rows or message patterns
      const lines = content.split('\n').filter(line => line.trim());
      console.log('Lines after filtering:', lines);
      const messages: Array<{sender: string, timestamp: string, original: string, translated: string, color: string}> = [];
      const senderColors: {[key: string]: string} = {};
      const colorPalette = [
        '#e8f5e8', // Light green
        '#e3f2fd', // Light blue
        '#fff3e0', // Light orange
        '#f3e5f5', // Light purple
        '#e0f2f1', // Light teal
        '#fce4ec', // Light pink
        '#f1f8e9', // Light lime
        '#e8eaf6', // Light indigo
        '#fff8e1', // Light amber
        '#efebe9'  // Light brown
      ];
      let colorIndex = 0;
      
      // First pass: collect all unique senders and assign colors
      const uniqueSenders = new Set<string>();
      
      // Look for table format (pipe-separated) or other patterns
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        // Skip empty lines, headers, and separators
        if (!line || 
            line.includes('Date/Time') || 
            line.includes('Sender') || 
            line.includes('Spanish') || 
            line.includes('English') || 
            line.includes('---') ||
            line.includes('Chunk') ||
            line.includes('Complete')) {
          continue;
        }
        
        let sender = '';
        
        // Check for system message pattern first
        if (line.includes('System Message')) {
          sender = 'System Message';
        }
        // Try table format (pipe-separated)
        else if (line.includes('|')) {
          const parts = line.split('|').map(p => p.trim()).filter(p => p);
          if (parts.length >= 3) {
            // The first column should contain timestamp + sender
            const firstColumn = parts[0] || '';
            
            // Look for UTC-4 pattern first, then phone number
            const utcMatch = firstColumn.match(/\(UTC-4\)/);
            if (utcMatch) {
              // Extract everything after (UTC-4) as the sender name
              const utcEnd = firstColumn.indexOf(utcMatch[0]) + utcMatch[0].length;
              sender = firstColumn.substring(utcEnd).trim();
            } else {
              // Fallback: look for phone number pattern (+1234567890) followed by name
              const phoneMatch = firstColumn.match(/\+[\d\s\-\(\)]+/);
              if (phoneMatch) {
                // Extract everything after the phone number as the sender name
                const phoneEnd = firstColumn.indexOf(phoneMatch[0]) + phoneMatch[0].length;
                sender = firstColumn.substring(phoneEnd).trim();
              } else {
                // Fallback: look for the last space-separated word that might be a name
                const words = firstColumn.split(/\s+/);
                if (words.length > 1) {
                  // Take the last word as sender name
                  sender = words[words.length - 1];
                } else {
                  sender = firstColumn;
                }
              }
            }
          }
        } 
        // Try colon format "Sender: message"
        else if (line.includes(':') && line.length > 10) {
          const colonIndex = line.indexOf(':');
          sender = line.substring(0, colonIndex).trim();
        }
        
        if (sender && sender.length > 0) {
          uniqueSenders.add(sender);
        }
      }
      
      // Assign colors to unique senders
      Array.from(uniqueSenders).forEach(sender => {
        if (!senderColors[sender]) {
          senderColors[sender] = colorPalette[colorIndex % colorPalette.length];
          colorIndex++;
        }
      });
      
      // Second pass: parse messages and assign consistent colors
      let currentMessage: {sender: string, timestamp: string, original: string, translated: string, color: string} | null = null;
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        // Skip empty lines, headers, and separators
        if (!line || 
            line.includes('Date/Time') || 
            line.includes('Sender') || 
            line.includes('Spanish') || 
            line.includes('English') || 
            line.includes('---') ||
            line.includes('Chunk') ||
            line.includes('Complete')) {
          continue;
        }
        
        let sender = '';
        let timestamp = '';
        let original = '';
        let translated = '';
        let isNewMessage = false;
        
        // Check for system message pattern first
        if (line.includes('System Message')) {
          // Save previous message if exists
          if (currentMessage && currentMessage.original) {
            messages.push(currentMessage);
          }
          
          // Start new system message
          sender = 'System Message';
          timestamp = '';
          original = '';
          translated = '';
          isNewMessage = true;
        }
        // Try table format (pipe-separated)
        else if (line.includes('|')) {
          const parts = line.split('|').map(p => p.trim()).filter(p => p);
          if (parts.length >= 3) {
            // Save previous message if exists
            if (currentMessage && currentMessage.original) {
              messages.push(currentMessage);
            }
            
            // First column: timestamp + sender
            const firstColumn = parts[0] || '';
            // Second column: original message
            original = parts[1] || '';
            // Third column: translated message
            translated = parts[2] || '';
            
            // Extract timestamp and sender from first column
            const utcMatch = firstColumn.match(/\(UTC-4\)/);
            if (utcMatch) {
              // Extract timestamp (everything before (UTC-4))
              timestamp = firstColumn.substring(0, firstColumn.indexOf(utcMatch[0])).trim();
              // Extract sender (everything after (UTC-4))
              const utcEnd = firstColumn.indexOf(utcMatch[0]) + utcMatch[0].length;
              sender = firstColumn.substring(utcEnd).trim();
            } else {
              // Fallback: look for phone number pattern
              const phoneMatch = firstColumn.match(/\+[\d\s\-\(\)]+/);
              if (phoneMatch) {
                // Extract timestamp (everything before phone number)
                timestamp = firstColumn.substring(0, firstColumn.indexOf(phoneMatch[0])).trim();
                // Extract sender (everything after phone number)
                const phoneEnd = firstColumn.indexOf(phoneMatch[0]) + phoneMatch[0].length;
                sender = firstColumn.substring(phoneEnd).trim();
              } else {
                // Fallback: split by spaces and take last word as sender
                const words = firstColumn.split(/\s+/);
                if (words.length > 1) {
                  timestamp = words.slice(0, -1).join(' ');
                  sender = words[words.length - 1];
                } else {
                  timestamp = firstColumn;
                  sender = 'Unknown';
                }
              }
            }
            isNewMessage = true;
          }
        } 
        // Try colon format "Sender: message"
        else if (line.includes(':') && line.length > 10) {
          // Save previous message if exists
          if (currentMessage && currentMessage.original) {
            messages.push(currentMessage);
          }
          
          const colonIndex = line.indexOf(':');
          sender = line.substring(0, colonIndex).trim();
          original = line.substring(colonIndex + 1).trim();
          translated = original; // If no translation provided, use original
          timestamp = new Date().toLocaleString();
          isNewMessage = true;
        }
        // Check for timestamp pattern (date/time format)
        else if (line.match(/\d{1,2}\/\d{1,2}\/\d{4}/) && currentMessage && currentMessage.sender === 'System Message') {
          // This is a timestamp for the current system message
          timestamp = line;
        }
        // Check for "ENGLISH TRANSLATION:" pattern
        else if (line.includes('ENGLISH TRANSLATION:') && currentMessage) {
          // This is the start of translation for current message
          // The next line should be the translation
          if (i + 1 < lines.length) {
            currentMessage.translated = lines[i + 1].trim();
            i++; // Skip the next line since we processed it
          }
        }
        // If we have a current message and this line doesn't match any pattern, it's content
        else if (currentMessage && currentMessage.sender === 'System Message' && !isNewMessage) {
          // This is content for the current system message
          if (currentMessage.original) {
            currentMessage.original += ' ' + line;
          } else {
            currentMessage.original = line;
          }
        }
        
        if (isNewMessage && sender) {
          currentMessage = {
            sender: sender,
            timestamp: timestamp || new Date().toLocaleString(),
            original: original,
            translated: translated || original,
            color: senderColors[sender] || colorPalette[0] // fallback color
          };
        }
      }
      
      // Don't forget the last message
      if (currentMessage && currentMessage.original) {
        messages.push(currentMessage);
      }
      
      // Debug: Log the parsed messages to see what's happening
      console.log('Parsed messages:', messages);
      console.log('Sender colors:', senderColors);
      console.log('Unique senders:', Array.from(uniqueSenders));
      
      return messages;
    };

    const messages = parseTranslatedContent(translatedContent);

    // Create HTML with chat bubble styling
    const htmlReportContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Cellebrite Translation - ${cellebriteFile?.name || 'Unknown File'}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
            background: #f0f2f5; 
            padding: 20px; 
            line-height: 1.4;
          }
          .header { 
            background: linear-gradient(135deg, #2c5aa0, #1e3a8a); 
            color: white; 
            padding: 30px; 
            border-radius: 12px; 
            margin-bottom: 30px; 
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            display: flex;
            align-items: center;
            gap: 20px;
          }
          .header-logo { 
            width: 60px; 
            height: 60px; 
            flex-shrink: 0;
            background: rgba(255,255,255,0.2);
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 18px;
            color: white;
            text-shadow: 0 1px 2px rgba(0,0,0,0.3);
          }
          .header-content { flex: 1; }
          .header h1 { font-size: 28px; margin-bottom: 15px; font-weight: 600; }
          .header-info { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; }
          .header-item { background: rgba(255,255,255,0.1); padding: 10px; border-radius: 8px; }
          .header-label { font-size: 12px; opacity: 0.8; text-transform: uppercase; letter-spacing: 0.5px; }
          .header-value { font-size: 14px; font-weight: 500; margin-top: 4px; }
          
          .chat-container { 
            max-width: 600px; 
            margin: 0 auto; 
            background: white; 
            border-radius: 12px; 
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            overflow: hidden;
          }
          
          .message { 
            display: flex; 
            margin-bottom: 20px; 
            padding: 15px 20px; 
            border-bottom: 1px solid #f0f0f0;
          }
          .message:last-child { border-bottom: none; }
          
          .message-avatar { 
            width: 40px; 
            height: 40px; 
            border-radius: 50%; 
            display: flex; 
            align-items: center; 
            justify-content: center; 
            color: white; 
            font-weight: bold; 
            font-size: 14px; 
            margin-right: 15px; 
            flex-shrink: 0;
            text-shadow: 0 1px 2px rgba(0,0,0,0.3);
          }
          
          .message-content { flex: 1; }
          .message-header { 
            display: flex; 
            justify-content: space-between; 
            align-items: center; 
            margin-bottom: 8px; 
          }
          .message-sender { 
            font-weight: 600; 
            color: #2c5aa0; 
            font-size: 14px; 
          }
          .message-time { 
            font-size: 12px; 
            color: #666; 
          }
          
          .message-text { 
            padding: 12px 16px; 
            border-radius: 18px; 
            border: 1px solid rgba(0,0,0,0.1); 
            margin-bottom: 8px;
            font-size: 14px;
            line-height: 1.5;
            color: #333;
          }
          
          .translation-text { 
            padding: 12px 16px; 
            border-radius: 18px; 
            border-left: 4px solid rgba(0,0,0,0.2); 
            font-size: 14px;
            line-height: 1.5;
            color: #333;
            font-style: italic;
          }
          
          .translation-label { 
            font-size: 11px; 
            color: #666; 
            text-transform: uppercase; 
            letter-spacing: 0.5px; 
            margin-bottom: 4px; 
            font-weight: 600;
          }
          
          .footer { 
            text-align: center; 
            padding: 20px; 
            color: #666; 
            font-size: 12px; 
            background: #f8f9fa; 
            border-top: 1px solid #e9ecef;
          }
          
          .no-messages { 
            text-align: center; 
            padding: 40px; 
            color: #666; 
            font-style: italic; 
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-logo">RTC</div>
          <div class="header-content">
            <h1>Cellebrite Translation Report</h1>
            <div class="header-info">
              <div class="header-item">
                <div class="header-label">Original File</div>
                <div class="header-value">${cellebriteFile?.name || 'Unknown'}</div>
              </div>
              <div class="header-item">
                <div class="header-label">Source Language</div>
                <div class="header-value">${selectedLanguage}</div>
              </div>
              <div class="header-item">
                <div class="header-label">Translation Date</div>
                <div class="header-value">${new Date().toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="chat-container">
          ${messages.length > 0 ? messages.map((msg, index) => `
            <div class="message">
              <div class="message-avatar" style="background: ${msg.color};">
                ${msg.sender.charAt(0).toUpperCase()}
              </div>
              <div class="message-content">
                <div class="message-header">
                  <div class="message-sender">${msg.sender}</div>
                  <div class="message-time">${msg.timestamp}</div>
                </div>
                <div class="message-text" style="background: ${msg.color};">
                  ${msg.original}
                </div>
                ${msg.translated && msg.translated !== msg.original ? `
                  <div class="translation-label">English Translation:</div>
                  <div class="translation-text" style="background: ${msg.color};">
                    ${msg.translated}
                  </div>
                ` : ''}
              </div>
            </div>
          `).join('') : `
            <div class="no-messages">
              <p>No messages found in the translation content.</p>
              <p>Raw content: ${translatedContent.replace(/\n/g, '<br>')}</p>
            </div>
          `}
        </div>
        
        <div class="footer">
          <p>Generated by RTC Chatbot Translation System</p>
          <p>This report contains translated communications from Cellebrite extraction</p>
        </div>
      </body>
      </html>
    `;

    // Create a blob with the HTML content
    const blob = new Blob([htmlReportContent], { type: 'text/html' });
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Cellebrite_Translation_${cellebriteFile?.name?.replace('.xlsx', '') || 'Unknown'}_${new Date().toISOString().split('T')[0]}.html`;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast({
      title: 'Download Started',
      description: 'HTML report download has started',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleCellebriteUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if it's an XLSX file
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      toast({
        title: 'Invalid File Type',
        description: 'Please select an XLSX file from Cellebrite export.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Check file size before upload (50MB limit for Cellebrite files)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      toast({
        title: 'File Too Large',
        description: `File size (${(file.size / (1024 * 1024)).toFixed(1)}MB) exceeds the 50MB limit.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setCellebriteFile(file);
    setShowLanguageSelector(true);
    
    // Reset the file input to prevent caching issues
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleCellebriteProcess = async () => {
    if (!cellebriteFile) return;

    setIsProcessingCellebrite(true);
    setShowLanguageSelector(false);
    setTranslatedContent('');
    setShowDownloadButton(false);
    setChunkProgress({current: 0, total: 0});
    setCompletedChunks(new Set());

    try {
      const formData = new FormData();
      formData.append('file', cellebriteFile);
      formData.append('language', selectedLanguage);

      const response = await fetch('/api/ollama/process-cellebrite', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Handle streaming response
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      let assistantMessage = '';
      const decoder = new TextDecoder();
      let buffer = '';
      let messageAdded = false;
      let currentChunk = 0;
      let totalChunks = 0;
      let chunkResults: {[key: number]: string} = {};
      let allChunksContent = ''; // Accumulate all chunks
      let htmlStructure: any[] = []; // Store the final HTML structure

      // Add initial message about Cellebrite processing
      addMessage('assistant', `Processing Cellebrite file "${cellebriteFile.name}" for ${selectedLanguage} translation...`);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data: ChatCompletionResponse = JSON.parse(line.slice(6));
              
              if (data.error) {
                throw new Error(data.error.message);
              }

              // Check for HTML content (new backend-generated HTML)
              if (data.type === 'html_content' && data.content) {
                setHtmlContent(data.content);
                console.log('Received HTML content from backend');
                // Don't add this to the streaming message, it's the final HTML
                continue;
              }

              // Check for HTML structure data (fallback)
              if (data.type === 'html_structure' && data.data) {
                htmlStructure = data.data;
                setHtmlStructureData(data.data);
                console.log('Received HTML structure:', htmlStructure);
                console.log('HTML structure data length:', data.data.length);
                console.log('First few messages:', data.data.slice(0, 3));
                // Don't add this to the streaming message, it's the final structured data
                continue;
              }

              if (data.choices && data.choices[0]?.delta?.content) {
                const content = data.choices[0].delta.content;
                assistantMessage += content;
                allChunksContent += content; // Accumulate all content
                setCurrentStreamingMessage(assistantMessage);
                
                // Check if this is a chunk separator
                if (content.includes('--- Chunk') && content.includes('Complete ---')) {
                  // Extract chunk number
                  const chunkMatch = content.match(/Chunk (\d+)/);
                  if (chunkMatch) {
                    const chunkNum = parseInt(chunkMatch[1]);
                    setCompletedChunks(prev => new Set([...prev, chunkNum]));
                    setChunkProgress(prev => ({...prev, current: chunkNum}));
                    console.log(`Chunk ${chunkNum} completed. Total completed: ${completedChunks.size + 1}`);
                  }
                }
                
                // Check if this is the total chunks info
                if (content.includes('Processing') && content.includes('chunks')) {
                  const chunksMatch = content.match(/(\d+) chunks/);
                  if (chunksMatch) {
                    totalChunks = parseInt(chunksMatch[1]);
                    setChunkProgress(prev => ({...prev, total: totalChunks}));
                    console.log(`Total chunks detected: ${totalChunks}`);
                  }
                }
                
                // Check if all chunks are complete
                if (content.includes('--- All Chunks Complete ---')) {
                  console.log('All chunks completed, showing download button');
                  setShowDownloadButton(true);
                }
                
                // Also check for chunk completion in the accumulated message
                const chunkCompleteMatch = assistantMessage.match(/--- Chunk (\d+) Complete ---/g);
                if (chunkCompleteMatch) {
                  const completedChunkNumbers = chunkCompleteMatch.map(match => {
                    const numMatch = match.match(/Chunk (\d+)/);
                    return numMatch ? parseInt(numMatch[1]) : 0;
                  }).filter(num => num > 0);
                  
                  if (completedChunkNumbers.length > 0) {
                    setCompletedChunks(prev => {
                      const newSet = new Set([...prev, ...completedChunkNumbers]);
                      console.log(`Updated completed chunks: ${Array.from(newSet).join(', ')}`);
                      return newSet;
                    });
                    setChunkProgress(prev => ({...prev, current: Math.max(...completedChunkNumbers)}));
                  }
                }
              }

              if (data.choices && data.choices[0]?.finish_reason) {
                // Don't add message here - wait for all chunks to complete
                setCurrentStreamingMessage('');
                // Don't break here - continue processing to get all chunks
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }

      // Use HTML content if available, otherwise fall back to other formats
      console.log('Final processing - htmlContent length:', htmlContent.length);
      console.log('Final processing - htmlStructureData length:', htmlStructureData.length);
      console.log('Final processing - allChunksContent length:', allChunksContent.length);
      
      if (htmlContent) {
        // Backend generated HTML - just show completion message
        addMessage('assistant', `Translation complete! HTML report generated with all translated messages.`);
        setTranslatedContent(htmlContent);
        console.log('Using backend-generated HTML content');
        
        // Show download button
        setShowDownloadButton(true);
        console.log('Download button enabled');
      } else if (htmlStructureData.length > 0) {
        // Convert HTML structure to display format
        const displayContent = htmlStructureData.map(msg => 
          `${msg.sender} | ${msg.timestamp} | ${msg.original_message} | ${msg.translation}`
        ).join('\n');
        
        addMessage('assistant', `Translation complete! Processed ${htmlStructureData.length} messages.\n\n${displayContent}`);
        setTranslatedContent(displayContent);
        console.log(`Final HTML structure: ${htmlStructureData.length} messages`);
        console.log('Sample display content:', displayContent.substring(0, 200));
        
        // Show download button
        setShowDownloadButton(true);
        console.log('Download button enabled');
      } else if (allChunksContent) {
        addMessage('assistant', allChunksContent);
        setTranslatedContent(allChunksContent);
        console.log(`Final content length: ${allChunksContent.length}`);
        console.log(`Total chunks: ${totalChunks}, Completed: ${completedChunks.size}`);
        
        // Show download button
        setShowDownloadButton(true);
        console.log('Download button enabled');
      }

      toast({
        title: 'Cellebrite Translation Complete',
        description: `Successfully processed and translated ${cellebriteFile.name} from ${selectedLanguage}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Error processing Cellebrite file:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to process Cellebrite file',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      addMessage('assistant', 'Sorry, I encountered an error processing the Cellebrite file. Please try again.');
    } finally {
      setIsProcessingCellebrite(false);
      setCurrentStreamingMessage('');
    }
  };

  return (
    <Box p={{ base: 1, md: 6 }} bg={bgColor} minH="100vh" h="100vh" overflow="hidden">
      <VStack spacing={{ base: 2, md: 6 }} maxW={containerMaxW} mx="auto" h="full">
        {/* Header */}
        <Card w="full" bg={cardBg} boxShadow="lg" flexShrink={0}>
          <CardBody p={{ base: 2, md: 6 }}>
            <Flex justify="space-between" align="center" direction={{ base: 'column', md: 'row' }} gap={2}>
              <VStack align="start" spacing={1}>
                <HStack spacing={2}>
                  <Box
                    w={{ base: '20px', md: '24px' }}
                    h={{ base: '20px', md: '24px' }}
                    bg="blue.600"
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    color="white"
                    fontSize={{ base: '10px', md: '12px' }}
                    fontWeight="bold"
                  >
                    RTC
                  </Box>
                  <Heading size={{ base: 'sm', md: 'lg' }} color="blue.600">
                    RTC Chatbot
                  </Heading>
                </HStack>
                <Text color="gray.600" fontSize="xs">
                  Powerful AI available in an airgapped environment for law enforcement use.
                </Text>
              </VStack>
              <Button
                leftIcon={<FiRefreshCw />}
                onClick={clearChat}
                size="xs"
                variant="outline"
                colorScheme="gray"
              >
                Clear
              </Button>
            </Flex>
          </CardBody>
        </Card>

        {/* Chat Container */}
        <Card w="full" bg={cardBg} boxShadow="lg" flex={1} h="auto" minH="0">
          <CardBody p={0} h="full">
            <VStack h="full" spacing={0}>
              {/* Messages Area */}
              <Box
                flex={1}
                w="full"
                overflowY="auto"
                p={{ base: 2, md: 6 }}
                css={{
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-track': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: useColorModeValue('#CBD5E0', '#4A5568'),
                    borderRadius: '24px',
                  },
                }}
              >
                <VStack spacing={3} align="stretch">
                  {messages.length === 0 && (
                    <Box textAlign="center" py={4}>
                      <Icon as={FiMessageSquare} w={8} h={8} color="gray.400" mb={2} />
                      <Text color="gray.500" fontSize="sm">
                        Powerful AI available in an airgapped environment for law enforcement use.
                      </Text>
                    </Box>
                  )}

                  {messages.map((message) => (
                    <Box
                      key={message.id}
                      alignSelf={message.role === 'user' ? 'flex-end' : 'flex-start'}
                      maxW={{ base: '95%', md: '80%' }}
                    >
                      <HStack spacing={2} align="start">
                        {message.role === 'assistant' && (
                          <Avatar size="xs" bg="blue.500" icon={<FiCpu />} />
                        )}
                        <VStack align={message.role === 'user' ? 'end' : 'start'} spacing={1} w="full">
                          <Box
                            bg={message.role === 'user' ? 'blue.500' : 'gray.100'}
                            color={message.role === 'user' ? 'white' : 'black'}
                            px={3}
                            py={2}
                            borderRadius="lg"
                            maxW="full"
                            wordBreak="break-word"
                          >
                            {message.role === 'user' ? (
                              <Text fontSize="xs" whiteSpace="pre-wrap">
                                {message.content}
                              </Text>
                            ) : (
                              <Box className="markdown-content">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                              </Box>
                            )}
                          </Box>
                          <HStack spacing={1}>
                            <Text fontSize="xs" color="gray.500">
                              {formatTime(message.timestamp)}
                            </Text>
                            {message.role === 'assistant' && (
                              <Badge size="xs" colorScheme="blue" variant="subtle">
                                RTC
                              </Badge>
                            )}
                          </HStack>
                        </VStack>
                        {message.role === 'user' && (
                          <Avatar size="xs" bg="green.500" icon={<FiUser />} />
                        )}
                      </HStack>
                    </Box>
                  ))}

                  {/* Streaming message */}
                  {currentStreamingMessage && (
                    <Box alignSelf="flex-start" maxW={{ base: '95%', md: '80%' }}>
                      <HStack spacing={2} align="start">
                        <Avatar size="xs" bg="blue.500" icon={<FiCpu />} />
                        <VStack align="start" spacing={1} w="full">
                          <Box
                            bg="gray.100"
                            px={3}
                            py={2}
                            borderRadius="lg"
                            maxW="full"
                            wordBreak="break-word"
                          >
                            <Box position="relative">
                              <Box className="markdown-content">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{currentStreamingMessage}</ReactMarkdown>
                              </Box>
                              <Spinner size="xs" position="absolute" top={1} right={1} />
                            </Box>
                          </Box>
                          <HStack spacing={1}>
                            <Text fontSize="xs" color="gray.500">
                              {formatTime(new Date())}
                            </Text>
                            <Badge size="xs" colorScheme="blue" variant="subtle">
                              RTC
                            </Badge>
                          </HStack>
                        </VStack>
                      </HStack>
                    </Box>
                  )}
                </VStack>
                <div ref={messagesEndRef} />
              </Box>

              <Divider />

              {/* Input Area */}
              <Box p={{ base: 2, md: 6 }} bg={cardBg} flexShrink={0} w="full">
                <VStack spacing={2} w="full">
                  {/* File Upload Section */}
                  <HStack spacing={2} w="full">
                    <Button
                      leftIcon={<FiUpload />}
                      onClick={triggerFileUpload}
                      isLoading={isAnalyzingFile}
                      variant="outline"
                      colorScheme="green"
                      size={{ base: 'sm', md: 'md' }}
                      flex={1}
                    >
                      {isAnalyzingFile ? 'Analyzing...' : 'Upload File (max 100MB)'}
                    </Button>
                    <Button
                      leftIcon={<FiGlobe />}
                      onClick={triggerCellebriteUpload}
                      isLoading={isProcessingCellebrite}
                      variant="outline"
                      colorScheme="purple"
                      size={{ base: 'sm', md: 'md' }}
                      flex={1}
                    >
                      {isProcessingCellebrite ? 'Translating...' : 'Translate Cellebrite (upload excel doc)'}
                    </Button>
                    {uploadedFile && (
                      <Badge colorScheme="green" variant="subtle">
                        {uploadedFile.name}
                      </Badge>
                    )}
                    {cellebriteFile && (
                      <Badge colorScheme="purple" variant="subtle">
                        {cellebriteFile.name}
                      </Badge>
                    )}
                    {fileAnalysis && (
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        onClick={clearFile}
                        title="Clear file from conversation"
                      >
                        Clear
                      </Button>
                    )}
                  </HStack>

                  {/* Language Selector for Cellebrite */}
                  {showLanguageSelector && cellebriteFile && (
                    <Box p={3} bg="purple.50" borderRadius="md" border="1px solid" borderColor="purple.200">
                      <VStack align="start" spacing={3}>
                        <HStack>
                          <Icon as={FiGlobe} color="purple.500" />
                          <Text fontSize="sm" fontWeight="medium">
                            Select Language for Translation
                          </Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.600">
                          File: {cellebriteFile.name}
                        </Text>
                        <Select
                          value={selectedLanguage}
                          onChange={(e) => setSelectedLanguage(e.target.value)}
                          size="sm"
                          bg="white"
                        >
                          {languageOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                        <HStack spacing={2}>
                          <Button
                            size="sm"
                            colorScheme="purple"
                            onClick={handleCellebriteProcess}
                            isLoading={isProcessingCellebrite}
                          >
                            {isProcessingCellebrite ? 'Processing...' : 'Start Translation'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setShowLanguageSelector(false);
                              setCellebriteFile(null);
                            }}
                          >
                            Cancel
                          </Button>
                        </HStack>
                      </VStack>
                    </Box>
                  )}

                  {/* Progress Indicator for Chunks */}
                  {isProcessingCellebrite && chunkProgress.total > 0 && (
                    <Box p={3} bg="purple.50" borderRadius="md" border="1px solid" borderColor="purple.200">
                      <VStack align="start" spacing={2}>
                        <HStack>
                          <Icon as={FiGlobe} color="purple.500" />
                          <Text fontSize="sm" fontWeight="medium">
                            Processing Chunks
                          </Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.600">
                          Chunk {chunkProgress.current} of {chunkProgress.total} completed
                        </Text>
                        <Box w="full" bg="purple.100" borderRadius="md" h="8px">
                          <Box 
                            bg="purple.500" 
                            h="100%" 
                            borderRadius="md" 
                            w={`${(chunkProgress.current / chunkProgress.total) * 100}%`}
                            transition="width 0.3s ease"
                          />
                        </Box>
                      </VStack>
                    </Box>
                  )}

                  {/* Download Button for Translated Content */}
                  {showDownloadButton && translatedContent && (
                    <Box p={3} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                      <VStack align="start" spacing={3}>
                        <HStack>
                          <Icon as={FiDownload} color="blue.500" />
                          <Text fontSize="sm" fontWeight="medium">
                            Translation Complete
                          </Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.600">
                          Download the translated content as an HTML report with chat bubbles
                        </Text>
                        <Button
                          leftIcon={<FiDownload />}
                          colorScheme="blue"
                          size="sm"
                          onClick={downloadAsWord}
                        >
                          Download as HTML Report
                        </Button>
                      </VStack>
                    </Box>
                  )}

                  {/* Manual Download Button - appears if automatic detection fails */}
                  {!showDownloadButton && translatedContent && !isProcessingCellebrite && (
                    <Box p={3} bg="yellow.50" borderRadius="md" border="1px solid" borderColor="yellow.200">
                      <VStack align="start" spacing={3}>
                        <HStack>
                          <Icon as={FiDownload} color="yellow.600" />
                          <Text fontSize="sm" fontWeight="medium">
                            Translation Available
                          </Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.600">
                          Translation completed but chunk detection may have failed. You can still download the content.
                        </Text>
                        <Button
                          leftIcon={<FiDownload />}
                          colorScheme="yellow"
                          size="sm"
                          onClick={downloadAsWord}
                        >
                          Download as HTML Report
                        </Button>
                      </VStack>
                    </Box>
                  )}
                  
                  {/* File Analysis Display */}
                  {fileAnalysis && (
                    <Box p={3} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                      <VStack align="start" spacing={1}>
                        <HStack>
                          <Icon as={FiFile} color="green.500" />
                          <Text fontSize="sm" fontWeight="medium">
                            File Analysis: {fileAnalysis.filename}
                          </Text>
                          <Badge colorScheme="green" size="xs">
                            Available
                          </Badge>
                        </HStack>
                        <Text fontSize="xs" color="gray.600">
                          Size: {(fileAnalysis.file_size_bytes / 1024).toFixed(1)}KB | 
                          Text: {fileAnalysis.extracted_text_length} chars | 
                          Tokens: {fileAnalysis.prompt_tokens} | 
                          Max: {fileAnalysis.max_tokens}
                        </Text>
                        {fileAnalysis.extracted_text && (
                          <Text fontSize="xs" color="green.600" fontStyle="italic">
                            ✓ File content loaded and available for reference in chat
                          </Text>
                        )}
                      </VStack>
                    </Box>
                  )}

                  <Textarea
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message here..."
                    resize="none"
                    rows={inputRows}
                    disabled={isLoading}
                    bg={cardBg}
                    borderColor={borderColor}
                    _focus={{
                      borderColor: 'blue.500',
                      boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
                    }}
                    w="full"
                    minH={{ base: '60px', md: '60px' }}
                    fontSize={{ base: 'sm', md: 'md' }}
                  />
                  <Button
                    leftIcon={<FiSend />}
                    onClick={handleSendMessage}
                    isLoading={isLoading}
                    colorScheme="blue"
                    size={{ base: 'sm', md: 'md' }}
                    disabled={!inputValue.trim()}
                    w="full"
                  >
                    Send Message
                  </Button>
                </VStack>
                
                {/* Hidden file inputs */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.txt,.csv,.tsv,.xlsx,.xls"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <input
                  ref={cellebriteFileInputRef}
                  type="file"
                  accept=".xlsx"
                  onChange={handleCellebriteUpload}
                  onClick={(e) => e.stopPropagation()}
                  style={{ display: 'none' }}
                />
              </Box>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
};

export default GptOssBeta; 